// CodeNekt for Fleet â€” dual-mode design tokens (single source of truth).
//
// `rawPalette` holds REAL hex values per mode and is consumed by:
//   - the MUI theme factory (createAppTheme) â€” needs concrete colors
//   - recharts components (SVG fills cannot use CSS variables reliably)
//   - the CSS-variable injector (applied to <html data-theme="...">)
//
// Surface / text / border tokens are exposed app-wide as CSS variables
// (--c-*), so every react-jss / inline-styled component re-themes the moment
// `data-theme` flips. Brand & status colors stay constant across modes.

export type ThemeMode = "light" | "dark";

export interface PaletteTokens {
  canvas: string; // page background
  surface: string; // card / module background
  surface2: string; // elevated / nested surface
  surfaceHover: string; // hover state on surfaces
  nav: string; // sidebar rail background
  navText: string; // sidebar text / icons
  navMuted: string; // sidebar inactive text
  border: string; // hairline borders
  borderStrong: string; // emphasized borders
  text: string; // primary text
  text2: string; // secondary text
  muted: string; // tertiary / metadata text
  shadow: string; // box-shadow color
}

// Brand & status colors â€” identical in both modes.
export const brand = {
  primary: "#2563EB",
  primaryDark: "#1D4ED8",
  electric: "#60A5FA",
  nektia: "#3B82F6",
  success: "#22C55E",
  warning: "#F59E0B",
  error: "#EF4444",
  violet: "#8B5CF6",
};

export const rawPalette: Record<ThemeMode, PaletteTokens> = {
  dark: {
    canvas: "#020617",
    surface: "#0E1626",
    surface2: "#1E293B",
    surfaceHover: "#243044",
    nav: "#0B1424",
    navText: "#F8FAFC",
    navMuted: "#A8B4C6",
    border: "#1E293B",
    borderStrong: "#334155",
    text: "#F8FAFC",
    text2: "#D5DEEA",
    muted: "#A0B0C4",
    shadow: "rgba(0, 0, 0, 0.45)",
  },
  light: {
    canvas: "#F1F5F9",
    surface: "#FFFFFF",
    surface2: "#F8FAFC",
    surfaceHover: "#EEF2F7",
    nav: "#0F172A",
    navText: "#F8FAFC",
    navMuted: "#A8B4C6",
    border: "#E2E8F0",
    borderStrong: "#CBD5E1",
    text: "#0F172A",
    text2: "#334155",
    muted: "#526378",
    shadow: "rgba(15, 23, 42, 0.08)",
  },
};

// Ordered series colors for charts (bars / lines / areas).
export const chartColors: Record<ThemeMode, string[]> = {
  dark: ["#3B82F6", "#60A5FA", "#22C55E", "#F59E0B", "#A78BFA", "#F472B6"],
  light: ["#2563EB", "#60A5FA", "#16A34A", "#F59E0B", "#7C3AED", "#DB2777"],
};

// Fixed color per vehicle status code (used by the fleet-status donut).
export const statusColors: Record<string, string> = {
  A: brand.primary, // Assigned
  F: brand.success, // Available / Free
  O: brand.warning, // Ordered
  W: brand.violet, // Pending validation
};

// CSS variable names mapped from token keys (kebab-cased under --c-*).
export const cssVarName: Record<keyof PaletteTokens, string> = {
  canvas: "--c-canvas",
  surface: "--c-surface",
  surface2: "--c-surface-2",
  surfaceHover: "--c-surface-hover",
  nav: "--c-nav",
  navText: "--c-nav-text",
  navMuted: "--c-nav-muted",
  border: "--c-border",
  borderStrong: "--c-border-strong",
  text: "--c-text",
  text2: "--c-text-2",
  muted: "--c-muted",
  shadow: "--c-shadow",
};

// Apply a mode's tokens to the document root as CSS variables.
export const applyThemeVars = (mode: ThemeMode): void => {
  if (typeof document === "undefined") return;
  const tokens = rawPalette[mode];
  const root = document.documentElement;
  (Object.keys(tokens) as (keyof PaletteTokens)[]).forEach((key) => {
    root.style.setProperty(cssVarName[key], tokens[key]);
  });
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
};
